import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Send, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/lib/utils2";

/**
 * Admin-triggered reminder controls (Phase 3 MVP — no scheduling yet).
 *  - Employee pre-holiday reminder for a single approved request
 *  - Supervisor upcoming summary (per supervisor)
 *  - Manager weekly overview (sends to all admins)
 *  - Reminder log table
 */
export default function RemindersAdmin({ reference }) {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [empReqId, setEmpReqId] = useState("");
  const [daysBefore, setDaysBefore] = useState(7);
  const [supId, setSupId] = useState("");
  const [supWindow, setSupWindow] = useState(14);

  const loadLogs = async () => {
    const { data } = await api.get("/admin/reminders/logs");
    setLogs(data);
  };
  const loadApproved = async () => {
    const { data } = await api.get("/holiday-requests", { params: { status: "approved" } });
    // Only future-starting approved requests are interesting for "pre-holiday".
    const todayIso = new Date().toISOString().slice(0, 10);
    setApprovedRequests(data.filter((r) => r.start_date >= todayIso).slice(0, 200));
  };
  useEffect(() => { loadLogs(); loadApproved(); }, []);

  const sendEmployee = async () => {
    if (!empReqId) { toast.error("Pick an approved request"); return; }
    setBusy("emp");
    try {
      await api.post(`/admin/reminders/employee/${empReqId}`, null, { params: { days_before: daysBefore } });
      toast.success("Contractor reminder sent");
      await loadLogs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const sendSupervisor = async () => {
    if (!supId) { toast.error("Pick a supervisor"); return; }
    setBusy("sup");
    try {
      await api.post(`/admin/reminders/supervisor/${supId}`, null, { params: { window_days: supWindow } });
      toast.success("Supervisor summary sent");
      await loadLogs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const sendManager = async () => {
    setBusy("man");
    try {
      const { data } = await api.post("/admin/reminders/manager/weekly");
      toast.success(`Manager weekly sent to ${data.sent} admin(s)`);
      await loadLogs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="reminders-admin">
      <div className="rounded-md border border-[#2A2A2A] bg-[#141414] text-[#A8A8A8] text-xs p-2.5 flex items-start gap-2" data-testid="reminders-note">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-[#C0C0C0]" />
        <div>
          <strong className="text-[#F5F5F5]">Manual triggers only.</strong> Automatic scheduled reminders aren&apos;t enabled yet. Use the buttons below to send reminder emails on demand. Every send is logged at the bottom of this tab.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Contractor pre-absence */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary"/>
              <h3 className="font-heading font-semibold text-sm">Contractor pre-absence</h3>
            </div>
            <p className="text-xs text-muted-foreground">Pick a future-starting approved notification and how many days ahead the reminder should reference.</p>
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Notification</Label>
              <Select value={empReqId} onValueChange={setEmpReqId}>
                <SelectTrigger data-testid="rem-emp-request"><SelectValue placeholder="Pick a request…"/></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {approvedRequests.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.first_name} {r.last_name} · {r.start_date} → {r.end_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Days before</Label>
              <Input data-testid="rem-emp-days" type="number" min={0} max={60} value={daysBefore} onChange={(e) => setDaysBefore(parseInt(e.target.value || "7", 10))}/>
              <Button data-testid="rem-emp-send" disabled={busy === "emp"} onClick={sendEmployee} className="w-full gap-1.5">
                <Send className="w-3.5 h-3.5"/> Send reminder
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Supervisor summary */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary"/>
              <h3 className="font-heading font-semibold text-sm">Supervisor summary</h3>
            </div>
            <p className="text-xs text-muted-foreground">Send the chosen supervisor a digest of their pending + upcoming approved absences.</p>
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Supervisor</Label>
              <Select value={supId} onValueChange={setSupId}>
                <SelectTrigger data-testid="rem-sup-sel"><SelectValue placeholder="Pick a supervisor…"/></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(reference?.supervisors || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — {s.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Window (days ahead)</Label>
              <Input data-testid="rem-sup-window" type="number" min={1} max={90} value={supWindow} onChange={(e) => setSupWindow(parseInt(e.target.value || "14", 10))}/>
              <Button data-testid="rem-sup-send" disabled={busy === "sup"} onClick={sendSupervisor} className="w-full gap-1.5">
                <Send className="w-3.5 h-3.5"/> Send summary
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Manager weekly */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary"/>
              <h3 className="font-heading font-semibold text-sm">Manager weekly overview</h3>
            </div>
            <p className="text-xs text-muted-foreground">Sends a snapshot (pending, approved, off today, depts under pressure) to every admin.</p>
            <Button data-testid="rem-man-send" disabled={busy === "man"} onClick={sendManager} className="w-full gap-1.5">
              <Send className="w-3.5 h-3.5"/> Send weekly to all admins
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Logs */}
      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Reminder log ({logs.length})</div>
        <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E]">
          <Table data-testid="reminders-log-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Sent at</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No reminders sent yet.</TableCell></TableRow>
              )}
              {logs.map((l) => (
                <TableRow key={l.id} data-testid={`rem-log-${l.id}`}>
                  <TableCell className="font-mono text-xs">{fmtDateTime(l.sent_at)}</TableCell>
                  <TableCell className="capitalize">{l.type.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-sm">{l.target_email}</TableCell>
                  <TableCell className="text-sm">{l.actor_name || "system"}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${
                      l.status === "sent" ? "bg-[#0F1F14] text-[#34D399] border-[#1F5E3D]" :
                      l.status === "failed" ? "bg-[#1F0A0A] text-[#F87171] border-[#5E1F1F]" :
                      "bg-[#141414] text-[#D0D0D0] border-[#2A2A2A]"
                    }`}>{l.status}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
